import { axiom } from '../../../lib/axiom';
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { api } from '../../../lib/api';
import { safeJsonParse } from '../../../lib/utils';
import { useWorkflowChat, ToolCall, ToolResult } from '../../../hooks/useWorkflowChat';
import { useStreams } from '../../../contexts/StreamsContext';

export interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system' | 'tool_use' | 'tool_result';
  content: string;
  timestamp: Date;
  thinking?: string | null;
  toolCalls?: ToolCall[];
  toolResults?: ToolResult[];
}

export type ChatScope = 'task' | 'stream';

interface UseChatSessionOptions {
  streamId: string;
  nodeId?: string; // Required for 'task' scope, undefined for 'stream' scope
  scope: ChatScope;
  /** Whether to persist input drafts to localStorage */
  persistDrafts?: boolean;
}

interface UseChatSessionReturn {
  // Message state
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  isLoadingHistory: boolean;
  sessionTokens: number;

  // Input state
  input: string;
  setInput: (value: string) => void;
  clearDraft: () => void;

  // Loading/error state
  isLoading: boolean;
  agentError: string | null;
  lastUserMessage: string;

  // Tool execution state
  pendingApproval: ToolCall | null;
  pendingNodeName: string | undefined;
  rateLimitApproval: {
    allowedCount: number;
    blockedToolCalls: ToolCall[];
    resourceType: 'node';
  } | null;
  autoApprove: boolean;
  isLooping: boolean;
  loopingStatus: string | null;

  // Streaming content
  streamingContent: string;
  streamingThinking: string;

  // Refs
  messagesEndRef: React.RefObject<HTMLDivElement>;
  inputRef: React.RefObject<HTMLTextAreaElement>;

  // Actions
  handleSend: () => Promise<void>;
  handleEditMessage: (messageId: string) => void;
  handleRetryError: () => void;
  handleDismissError: () => void;
  handleToolApproval: () => Promise<void>;
  handleToolRejection: () => Promise<void>;
  handleRateLimitApproval: (allow: boolean) => Promise<void>;
  setAutoApprove: (value: boolean) => void;
  stopAgentLoop: () => void;
}

/**
 * Hook for managing chat session state and actions.
 * Unifies the shared logic between TaskChat (task scope) and BotChat (stream scope).
 */
export function useChatSession(options: UseChatSessionOptions): UseChatSessionReturn {
  const { streamId, nodeId, scope, persistDrafts = scope === 'task' } = options;

  // ============================================================================
  // Message State
  // ============================================================================

  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [sessionTokens, setSessionTokens] = useState(0);

  // ============================================================================
  // Input State with Optional Draft Persistence
  // ============================================================================

  const draftKey = persistDrafts
    ? `chatDraft_${streamId}_${nodeId || 'stream'}`
    : null;

  const [input, setInputState] = useState(() => {
    if (!draftKey) return '';
    try {
      return localStorage.getItem(draftKey) || '';
    } catch {
      return '';
    }
  });

  const setInput = useCallback((value: string) => {
    setInputState(value);
    if (draftKey) {
      try {
        if (value) {
          localStorage.setItem(draftKey, value);
        } else {
          localStorage.removeItem(draftKey);
        }
      } catch {
        // Ignore localStorage errors
      }
    }
  }, [draftKey]);

  const clearDraft = useCallback(() => {
    if (draftKey) {
      try {
        localStorage.removeItem(draftKey);
      } catch {
        // Ignore localStorage errors
      }
    }
  }, [draftKey]);

  // Reload draft when task/stream changes
  useEffect(() => {
    if (draftKey) {
      try {
        setInputState(localStorage.getItem(draftKey) || '');
      } catch {
        setInputState('');
      }
    } else {
      setInputState('');
    }
  }, [draftKey]);

  // ============================================================================
  // Loading/Error State
  // ============================================================================

  const [isLoading, setIsLoading] = useState(false);
  const [agentError, setAgentError] = useState<string | null>(null);
  const [lastUserMessage, setLastUserMessage] = useState<string>('');

  // ============================================================================
  // Refs
  // ============================================================================

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // ============================================================================
  // Stream Context
  // ============================================================================

  const { currentStreamData, autoLayoutStream } = useStreams();

  // ============================================================================
  // Tool Execution (WebSocket-based via useWorkflowChat)
  // ============================================================================

  const agentHookOptions = useMemo(() => ({
    streamId,
    nodeId,
    onAutoLayout: async () => {
      await autoLayoutStream(window.innerWidth - 400, window.innerHeight - 200);
    },
  }), [streamId, nodeId, autoLayoutStream]);

  const {
    pendingApproval,
    rateLimitApproval,
    autoApprove,
    isLooping,
    loopingStatus,
    runAgentLoop,
    continueAfterApproval,
    handleRateLimitApproval: handleRateLimitApprovalBase,
    setAutoApprove,
    stopAgentLoop,
    streamingContent,
    streamingThinking,
  } = useWorkflowChat(agentHookOptions);

  // Find node name for pending approval display
  const pendingNodeName = useMemo(() => {
    if (!pendingApproval) return undefined;
    const targetNodeId = (pendingApproval.input as { node_id: string }).node_id;
    return currentStreamData?.nodes.find(n => n.id === targetNodeId)?.name;
  }, [pendingApproval, currentStreamData]);

  // ============================================================================
  // Load Chat History
  // ============================================================================

  useEffect(() => {
    const loadHistory = async () => {
      setIsLoadingHistory(true);
      try {
        // For task scope, load task-specific messages
        // For stream scope, load stream-level messages (no nodeId filter)
        const history = scope === 'task'
          ? await api.chat.history(streamId, nodeId)
          : await api.chat.history(streamId);

        // Filter to stream-level only for stream scope
        const filteredHistory = scope === 'stream'
          ? history.filter(msg => !msg.node_id)
          : history;

        const formattedMessages: Message[] = filteredHistory.map((msg) => {
          let toolCalls: ToolCall[] | undefined;
          let toolResults: ToolResult[] | undefined;

          if (msg.tool_calls && msg.tool_calls.length > 0) {
            toolCalls = msg.tool_calls.map((tc) => ({
              id: tc.id,
              name: tc.name,
              input: safeJsonParse<Record<string, unknown>>(tc.input, {}),
              skipped: tc.status === 'skipped',
            }));

            toolResults = msg.tool_calls.map((tc) => ({
              tool_call_id: tc.id,
              success: tc.status === 'success',
              result: safeJsonParse<unknown>(tc.result, undefined),
              error: tc.error || undefined,
            }));
          }

          // For messages with tool calls but no text, show empty string
          const content = msg.content === '(Tool use only)' && toolCalls ? '' : msg.content;

          return {
            id: msg.id,
            role: msg.role as 'user' | 'assistant' | 'system' | 'tool_use' | 'tool_result',
            content,
            timestamp: new Date(msg.created_at),
            thinking: msg.thinking ?? null,
            toolCalls,
            toolResults,
          };
        });

        setMessages(formattedMessages);

        // Calculate total tokens from chat history
        const totalHistoryTokens = filteredHistory.reduce(
          (sum, msg) => sum + (msg.tokens_input || 0) + (msg.tokens_output || 0),
          0
        );
        setSessionTokens(totalHistoryTokens);
      } catch (err) {
        axiom.error('Failed to load chat history', { error: err instanceof Error ? err.message : String(err) });
        setMessages([]);
        setSessionTokens(0);
      } finally {
        setIsLoadingHistory(false);
      }
    };

    loadHistory();
  }, [streamId, nodeId, scope]);

  // ============================================================================
  // Reset State on Context Change
  // ============================================================================

  useEffect(() => {
    setIsLoading(false);
    setAgentError(null);
    setLastUserMessage('');
  }, [streamId, nodeId]);

  // ============================================================================
  // Auto-scroll and Focus
  // ============================================================================

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // ============================================================================
  // Message Handlers
  // ============================================================================

  const handleSend = useCallback(async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: input.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    const messageText = input.trim();
    setLastUserMessage(messageText);
    setInput('');
    clearDraft();
    setIsLoading(true);
    setAgentError(null);

    try {
      const result = await runAgentLoop(messageText);

      if (result.paused) {
        // Waiting for approval - show partial message
        const assistantMessage: Message = {
          id: `msg-${Date.now()}`,
          role: 'assistant',
          content: result.content || 'Waiting for approval...',
          timestamp: new Date(),
          toolCalls: result.allToolCalls,
          toolResults: result.allToolResults,
        };
        setMessages(prev => [...prev, assistantMessage]);
      } else if (result.stopped) {
        // User stopped - show partial content with indicator
        if (result.content) {
          const assistantMessage: Message = {
            id: `msg-${Date.now()}`,
            role: 'assistant',
            content: result.content + '\n\n*[Stopped]*',
            timestamp: new Date(),
            toolCalls: result.allToolCalls,
            toolResults: result.allToolResults,
          };
          setMessages(prev => [...prev, assistantMessage]);
        }
      } else if (result.complete) {
        // Complete - show final message
        const assistantMessage: Message = {
          id: `msg-${Date.now()}`,
          role: 'assistant',
          content: result.content || '',
          timestamp: new Date(),
          thinking: result.thinking,
          toolCalls: result.allToolCalls,
          toolResults: result.allToolResults,
        };
        setMessages(prev => [...prev, assistantMessage]);
      } else if (result.error) {
        throw new Error(result.error);
      }
    } catch (err) {
      axiom.error('Chat error', { error: err instanceof Error ? err.message : String(err) });
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      setAgentError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, runAgentLoop, setInput, clearDraft]);

  const handleEditMessage = useCallback((messageId: string) => {
    const messageIndex = messages.findIndex(m => m.id === messageId);
    if (messageIndex === -1) return;

    const message = messages[messageIndex];
    if (message.role !== 'user') return;

    setInput(message.content);
    setMessages(prev => prev.slice(0, messageIndex));
    inputRef.current?.focus();
  }, [messages, setInput]);

  const handleRetryError = useCallback(() => {
    setAgentError(null);
    if (lastUserMessage) {
      setInput(lastUserMessage);
      inputRef.current?.focus();
    }
  }, [lastUserMessage, setInput]);

  const handleDismissError = useCallback(() => {
    setAgentError(null);
  }, []);

  // ============================================================================
  // Approval Handlers
  // ============================================================================

  const handleToolApproval = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await continueAfterApproval(true);
      if (result?.complete) {
        const assistantMessage: Message = {
          id: `msg-${Date.now()}`,
          role: 'assistant',
          content: result.content || '',
          timestamp: new Date(),
          thinking: result.thinking,
          toolCalls: result.allToolCalls,
          toolResults: result.allToolResults,
        };
        setMessages(prev => [...prev, assistantMessage]);
      }
    } catch (err) {
      axiom.error('Approval continuation error', { error: err instanceof Error ? err.message : String(err) });
    } finally {
      setIsLoading(false);
    }
  }, [continueAfterApproval]);

  const handleToolRejection = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await continueAfterApproval(false);
      if (result?.complete) {
        const assistantMessage: Message = {
          id: `msg-${Date.now()}`,
          role: 'assistant',
          content: result.content || '',
          timestamp: new Date(),
          thinking: result.thinking,
          toolCalls: result.allToolCalls,
          toolResults: result.allToolResults,
        };
        setMessages(prev => [...prev, assistantMessage]);
      }
    } catch (err) {
      axiom.error('Rejection continuation error', { error: err instanceof Error ? err.message : String(err) });
    } finally {
      setIsLoading(false);
    }
  }, [continueAfterApproval]);

  const handleRateLimitApproval = useCallback(async (allow: boolean) => {
    setIsLoading(true);
    try {
      const result = await handleRateLimitApprovalBase(allow);
      if (result?.complete) {
        const assistantMessage: Message = {
          id: `msg-${Date.now()}`,
          role: 'assistant',
          content: result.content || '',
          timestamp: new Date(),
          thinking: result.thinking,
          toolCalls: result.allToolCalls,
          toolResults: result.allToolResults,
        };
        setMessages(prev => [...prev, assistantMessage]);
      }
    } catch (err) {
      axiom.error('Rate limit approval error', { error: err instanceof Error ? err.message : String(err) });
    } finally {
      setIsLoading(false);
    }
  }, [handleRateLimitApprovalBase]);

  return {
    // Message state
    messages,
    setMessages,
    isLoadingHistory,
    sessionTokens,

    // Input state
    input,
    setInput,
    clearDraft,

    // Loading/error state
    isLoading,
    agentError,
    lastUserMessage,

    // Tool execution state
    pendingApproval,
    pendingNodeName,
    rateLimitApproval,
    autoApprove,
    isLooping,
    loopingStatus,

    // Streaming content
    streamingContent,
    streamingThinking,

    // Refs
    messagesEndRef,
    inputRef,

    // Actions
    handleSend,
    handleEditMessage,
    handleRetryError,
    handleDismissError,
    handleToolApproval,
    handleToolRejection,
    handleRateLimitApproval,
    setAutoApprove,
    stopAgentLoop,
  };
}
